import { monitorHasSecondlyNoiseConfig } from 'app/business-logic/helpers/monitorHasSecondlyNoiseConfig';
import { useEffect, useMemo } from 'react';
import { LogManager } from 'core/logging/LogManager';
import { useMonitors } from 'app/hooks/useMonitors';
import lodash from 'lodash';
import { useMonitorLocations } from 'app/hooks/useMonitorLocations';
import { useMonitorsWithUniqueConfig } from './hooks/useMonitorsWithUniqueConfig';

const logger = LogManager.getLogger('useNoiseMonitors');

export function useNoiseMonitors() {
  const { data: monitors } = useMonitors();
  const locations = useMonitorLocations();
  const secondlyNoiseMonitors = useMemo(
    () =>
      monitors.flatMap(monitor => {
        if (!monitorHasSecondlyNoiseConfig(monitor)) return [];
        const { location } = locations?.find(l => l.id === monitor.id) ?? {};
        if (!location) return [];
        return {
          ...monitor,
          location,
        };
      }),
    [locations, monitors]
  );
  const { uniqueConfigurations, duplicateConfigurations } = useMonitorsWithUniqueConfig(secondlyNoiseMonitors);

  useLogWarnings(duplicateConfigurations);

  return uniqueConfigurations;
}

const { debounce } = lodash;
const warn = debounce(logger.warn);

function useLogWarnings(
  duplicateConfigurations: ReturnType<typeof useMonitorsWithUniqueConfig>['duplicateConfigurations']
) {
  const warnings = useMemo(
    () =>
      duplicateConfigurations
        .map(
          ({ monitor, firstInstance }) =>
            `Process "${monitor.name}" (${monitor.id}) has been configured with the same Omnis device ID as "${firstInstance.name}" (${firstInstance.id})`
        )
        .join('\n'),
    [duplicateConfigurations]
  );

  useEffect(() => {
    if (warnings.length) {
      warn(warnings);
    }
  }, [warnings]);
}
